import React, { useEffect, useMemo, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PartnerCombobox } from "@/components/PartnerCombobox";
import { todayStr } from "@/components/DateRangeFilter";
import { toast } from "sonner";

export const pph23OnInvoice = (inv) =>
  (inv.tax_deductions || []).filter((d) => (d.name || "").toLowerCase().replace(/\s/g, "").includes("pph23")).reduce((s, d) => s + (d.amount || 0), 0);
export const pph23Remaining = (inv, receipts) =>
  Math.max(0, (inv.pph23_amount || 0) - pph23OnInvoice(inv) - receipts.filter((r) => r.invoice_id === inv.id).reduce((s, r) => s + (r.pph23_withheld || 0), 0));

function InvoiceRow({ inv, checked, onToggle, pph23 }) {
  return (
    <label className={`flex items-center gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer transition-colors ${checked ? "bg-emerald-50" : "hover:bg-slate-50"}`} data-testid={`mrcp-row-${inv.id}`}>
      <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`mrcp-check-${inv.id}`} />
      <div className="flex-1 min-w-0">
        <div className="font-mono-plex text-xs font-medium">{inv.number}{inv.customer_po_no && <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">PO {inv.customer_po_no}</span>}</div>
        <div className="text-[11px] text-slate-500">Tgl {formatDate(inv.date)} · Jatuh tempo {formatDate(inv.due_date)}{pph23 > 0 && ` · PPh 23 ${formatIDR(pph23)}`}</div>
      </div>
      <div className="tabular-nums text-sm font-semibold">{formatIDR(inv.balance)}</div>
    </label>
  );
}

export function MultiReceiptDialog({ open, onClose, partners, cashBanks, onSaved }) {
  const [partner, setPartner] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [checked, setChecked] = useState({});
  const [form, setForm] = useState({ date: todayStr(), cash_bank_id: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [withPph23, setWithPph23] = useState(true);

  useEffect(() => {
    if (!open) { setPartner(null); setInvoices([]); setChecked({}); setForm({ date: todayStr(), cash_bank_id: "", notes: "" }); }
  }, [open]);

  useEffect(() => {
    if (!partner) { return; }
    Promise.all([api.get("/sales/invoices"), api.get("/sales/receipts")]).then(([i, r]) => {
      setInvoices(i.data.filter((x) => x.partner_id === partner.id && (x.balance || 0) > 0.01).sort((a, b) => (a.date || "").localeCompare(b.date || "")));
      setReceipts(r.data); setChecked({});
    });
  }, [partner]);

  const selected = useMemo(() => invoices.filter((i) => checked[i.id]), [invoices, checked]);
  const total = selected.reduce((s, i) => s + (i.balance || 0), 0);
  const pph23Of = (i) => (withPph23 ? Math.min(pph23Remaining(i, receipts), i.balance || 0) : 0);
  const pph23 = selected.reduce((s, i) => s + pph23Of(i), 0);
  const hasPph23 = selected.some((i) => pph23Remaining(i, receipts) > 0);
  const allChecked = invoices.length > 0 && selected.length === invoices.length;

  const save = async () => {
    if (!selected.length) return toast.error("Centang minimal satu faktur");
    if (!form.cash_bank_id) return toast.error("Pilih akun Kas/Bank");
    setSaving(true);
    try {
      const lines = selected.map((i) => ({ invoice_id: i.id, amount: i.balance, pph23_withheld: pph23Of(i) }));
      const r = await api.post("/sales/receipts/multi", { partner_id: partner.id, ...form, lines });
      toast.success(`Penerimaan ${r.data.number} tersimpan untuk ${selected.length} faktur`);
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="multi-receipt-dialog">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Penerimaan Multi Faktur</DialogTitle></DialogHeader>
        <div className="min-h-0 overflow-y-auto px-6 pt-4 pb-6 space-y-3">
          <div><Label>Pelanggan</Label><PartnerCombobox partners={partners} value={partner?.id || ""} onChange={setPartner} testid="mrcp-partner" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mrcp-date" /></div>
            <div><Label>Kas/Bank</Label>
              <Select value={form.cash_bank_id} onValueChange={(v) => setForm({ ...form, cash_bank_id: v })}>
                <SelectTrigger data-testid="mrcp-cb-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{cashBanks.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {partner && (
            <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="mrcp-invoice-list">
              <label className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 cursor-pointer">
                <Checkbox checked={allChecked} onCheckedChange={(v) => setChecked(v ? Object.fromEntries(invoices.map((i) => [i.id, true])) : {})} data-testid="mrcp-check-all" />
                Faktur belum lunas ({invoices.length})<span className="ml-auto">Sisa Tagihan</span>
              </label>
              {invoices.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">Tidak ada faktur belum lunas untuk pelanggan ini.</div>
                : invoices.map((i) => <InvoiceRow key={i.id} inv={i} checked={!!checked[i.id]} pph23={pph23Remaining(i, receipts)} onToggle={(v) => setChecked({ ...checked, [i.id]: !!v })} />)}
            </div>
          )}
          {hasPph23 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mrcp-pph23-toggle-label">
              <Checkbox checked={withPph23} onCheckedChange={(v) => setWithPph23(!!v)} data-testid="mrcp-pph23-toggle" />
              Pelanggan memotong PPh 23 atas faktur jasa terpilih
            </label>
          )}
          <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="mrcp-notes" /></div>
          <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 space-y-1 text-sm" data-testid="mrcp-summary">
            <div className="flex justify-between"><span>Total Faktur Dipilih ({selected.length})</span><span className="tabular-nums font-semibold" data-testid="mrcp-total">{formatIDR(total)}</span></div>
            {pph23 > 0 && <div className="flex justify-between text-slate-600"><span>PPh 23 Dipotong</span><span className="tabular-nums" data-testid="mrcp-pph23">-{formatIDR(pph23)}</span></div>}
            <div className="flex justify-between text-emerald-900 font-bold text-base"><span>Kas Diterima</span><span className="tabular-nums" data-testid="mrcp-net">{formatIDR(total - pph23)}</span></div>
          </div>
          <Button className="w-full bg-amber-600 hover:bg-amber-700" onClick={save} disabled={saving} data-testid="mrcp-save-btn">{saving ? "Menyimpan…" : "Simpan Penerimaan"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
